import { DatePipe, NgClass, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild
} from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { AvailabilitySpaBooking } from '../../../../../../core/model/availability-spa-booking.model';
import { SignInComponent } from '../../../account/sign-in/sign-in.component';
import { emulateClick } from '../../../core/a11y';
import { DrawerService } from '../../../drawer/drawer.service';
import { NotificationService } from '../../../notification/notification.service';
import { Custom, DatePickerComponent } from '../../date-picker/date-picker.component';
import { HoverGradientDirective } from '../../directives/hover-gradient.directive';
import { ProgressClickDirective } from '../../directives/progress-click.directive';
import { Center } from '../../model/center.model';
import { AccountService } from '../../services/account.service';
import { AuthService } from '../../services/auth.service';
import { Product } from '../../model/product.model';
import { Invitation, SpaBooking } from '../../../../../../core/model/cart/cart-item.model';
import { Gender } from '../../../../../../core/profile/gender';
import { CartService } from '../../services/cart.service';

type InvitationForm = FormGroup<{
  gender: FormControl<Gender>,
  firstname: FormControl<string>,
  lastname: FormControl<string>,
  email: FormControl<string>
}>

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-spa-booking',
  templateUrl: 'spa-booking.component.html',
  imports: [
    HoverGradientDirective,
    DatePickerComponent,
    NgxTolgeeModule,
    NgClass,
    ReactiveFormsModule,
    DatePipe,
    ProgressClickDirective,
    NgOptimizedImage
  ]
})
export class SpaBookingComponent {
  accountService = inject(AccountService);
  authService = inject(AuthService);
  drawerService = inject(DrawerService);
  cartService = inject(CartService);
  notificationService = inject(NotificationService);
  center = input.required<Center>();
  product = input.required<Product>();
  listenCart = input<boolean>(false);
  invitations = input<Invitation[]>([]);
  isInitiation = computed(() => {
    const product = this.product();
    return product.linkedProducts.length === 1 && product.linkedProducts[0].erpId === 106
  });
  isRituel = computed(() => {
    const product = this.product();
    return product.linkedProducts.length > 1 && product.linkedProducts[0].erpId === 106
  });
  showPlanning = linkedSignal<boolean, boolean>({
    source: this.isInitiation,
    computation: isInitiation => !isInitiation
  });
  invitationListForm = new FormArray<InvitationForm>([]);

  validated = output<SpaBooking>();
  cancelled = output<void>();
  nbInvitations = output<number>();
  validatedInvitations = signal<Invitation[] | null>(null);
  availableDays = signal<Custom<Date>[]>([]);
  selectedDay = signal<{ availabilities: AvailabilitySpaBooking, durations: Record<number, number> } | null>(null);
  availableSlots = computed(() => {
    const availabilities = this.selectedDay()?.availabilities;
    if (!availabilities) {
      return null;
    }
    const isInitiation = this.isInitiation();
    const isRituel = this.isRituel();
    const step = isInitiation ? 60 : 15;
    const slots: Date[] = []
    for (const slot of availabilities.slots) {
      const startTimeArray = slot.from.split(':').map(Number);
      let [startHour] = startTimeArray;
      const [, startMinute] = startTimeArray;
      const endTimeArray = slot.to.split(':').map(Number);
      let [endHour] = endTimeArray;
      const [, endMinute] = endTimeArray;
      if (isRituel) {
        startHour += 1;
        endHour += 1;
      }
      const start = Date.UTC(1970, 0, 1, startHour, Math.ceil(startMinute / step) * step, 0)
      const max = new Date(Date.UTC(1970, 0, 1, endHour, endMinute + 1, 0));
      const time = new Date(start);
      while (time.getTime() + availabilities.duration * 60 * 1000 <= max.getTime()) {
        slots.push(new Date(time));
        time.setUTCMinutes(time.getUTCMinutes() + step);
      }
    }
    slots.sort((a, b) => a.getTime() - b.getTime());
    return slots.map(date => {
      const hour = date.getUTCHours().toLocaleString(undefined, { minimumIntegerDigits: 2 });
      const minute = date.getUTCMinutes().toLocaleString(undefined, { minimumIntegerDigits: 2 });
      return `${hour}:${minute}`;
    })
  });
  cartSaving = computed(() => this.listenCart() && this.cartService.saving())
  selectedSlot = signal<string | null>(null);
  actualSlot = signal<{ erpId: number, start: string }[] | null>(null);
  selectSlotProgress = signal(false);
  updatePlanningProgress = signal(false);
  selectedPeriod = computed(() => {
    const isInitiation = this.isInitiation();
    const from = new Date();
    if (!isInitiation) {
      from.setDate(from.getDate() + 2);
    }
    const to = new Date(from)
    to.setMonth(to.getMonth() + 2);
    return { from, to };
  });
  spaEmail = computed(() => {
    const center = this.center();
    switch (center.erpId) {
    case 1:
      return 'boulogne@lescentciels.fr';
    case 2:
      return 'republique@lescentciels.fr';
    case 3:
      return 'lille@lescentciels.fr';
    case 4:
      return 'strasbourg@lescentciels.fr';
    case 5:
      return 'lyon@lescentciels.fr';
    case 6:
      return 'bordeaux@lescentciels.fr';
    default:
      return 'XXXXXX';
    }
  });
  timeSlotsView = viewChild<ElementRef<HTMLElement>>('timeSlots');
  protected readonly emulateClick = emulateClick;
  protected readonly Gender = Gender;

  constructor() {
    effect(() => {
      const invitations = this.validatedInvitations();
      if (!invitations) {
        return;
      }
      this.nbInvitations.emit(invitations.length)
    });
    effect(() => {
      const invitations = this.invitations();
      this.invitationListForm.clear();
      for (const s of invitations) {
        this.invitationListForm.push(new FormGroup({
          gender: new FormControl<Gender>(s.gender, { validators: [Validators.required], nonNullable: true }),
          firstname: new FormControl<string>(s.firstname, { validators: [Validators.required], nonNullable: true }),
          lastname: new FormControl<string>(s.lastname, { validators: [Validators.required], nonNullable: true }),
          email: new FormControl<string>(s.email, {
            validators: [Validators.required, Validators.email],
            nonNullable: true
          }),
        }));
      }

    });
    effect(async () => {
      const product = this.product();
      const invitations = this.validatedInvitations() ?? []
      await this.updatePlanning(product.linkedProducts.map(p => p.erpId), invitations);
    });

    effect(() => {
      const target = this.timeSlotsView()?.nativeElement;
      if (!this.availableSlots()?.length || !target) {
        return;
      }
      // Target is outside the viewport from the bottom
      if (target.getBoundingClientRect().bottom > window.innerHeight) {
        target.scrollIntoView(false);
      }
      // Target is outside the view from the top
      if (target.getBoundingClientRect().top < 0) {
        target.scrollIntoView();
      }

    });
    effect(async () => {
      const user = this.authService.user();
      const center = this.center();
      const product = this.product();
      const selectedDay = this.selectedDay();
      const selectedSlot = this.selectedSlot();
      if (!user?.authCompleted || !user.erpId || !selectedDay || !selectedSlot) {
        this.actualSlot.set(null)
        return;
      }
      const isRituel = this.isRituel();
      try {
        const dateTimeFormat = new Intl.DateTimeFormat('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
        });
        const date = new Date();
        const timeArray = selectedSlot.split(':').map(Number);
        let [h] = timeArray;
        const [, m] = timeArray;
        if (isRituel) {
          h -= 1;
        }
        date.setHours(h, m);
        const products: { erpId: number, start: string }[] = [];
        for (const p of product.linkedProducts) {
          const tmpDate = new Date(date);
          if (p.erpId === 106) {
            tmpDate.setMinutes(0);
          }
          products.push({
            erpId: p.erpId,
            start: dateTimeFormat.format(tmpDate)
          });
          date.setMinutes(date.getMinutes() + selectedDay.durations[p.erpId]);
        }

        const actualSlot = await this.accountService.planSpa(selectedDay.availabilities.date, user.erpId,
          products, center.erpId, this.invitationListForm.getRawValue());
        this.actualSlot.set(actualSlot);
      } catch (err) {
        console.error(err);
        this.notificationService.open({
          type: 'error',
          title: 'cannotPlan',
          message: 'cannotPlanDescription'
        });
      } finally {
        this.selectSlotProgress.set(false);
      }
    }, { allowSignalWrites: true });
  }

  planSpa = async (): Promise<void> => {
    const product = this.product();
    const selectedDay = this.selectedDay();
    const actualSlot = this.actualSlot();
    if (!product.linkedProducts.length || !selectedDay || !actualSlot) {
      return;
    }
    this.validated.emit({
      date: selectedDay.availabilities.date,
      products: actualSlot,
      created: new Date().toISOString(),
      invitations: this.validatedInvitations() ?? []
    });
    this.actualSlot.set(null)
  }

  selectDay($event: unknown): void {
    this.selectedDay.set($event as { availabilities: AvailabilitySpaBooking, durations: Record<number, number> });
    this.selectedSlot.set(null);
    this.actualSlot.set(null);
  }

  /**
   * New time slot selected by the user
   * @param slot The time slot
   */
  selectSlot(slot: string): void {
    if (!this.authService.user()?.authCompleted) {
      this.drawerService.open({ component: SignInComponent, inputs: { inDrawer: true }, style: 'transparent' });
    }
    this.selectedSlot.set(null);
    this.selectSlotProgress.set(true);
    this.selectedSlot.set(slot);
    this.actualSlot.set(null);
  }

  addInvitation(): void {
    this.invitationListForm.push(new FormGroup({
      gender: new FormControl<Gender>(Gender.Male, { validators: [Validators.required], nonNullable: true }),
      firstname: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
      lastname: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
      email: new FormControl<string>('', { validators: [Validators.required, Validators.email], nonNullable: true }),
    }))
  }

  womanOnly(slot: string): boolean {
    if (![1, 2].includes(this.center().erpId)) {
      return false;
    }
    const date = this.selectedDay()?.availabilities?.date;
    if (!date || [0, 6].includes(new Date(date).getDay())) {
      return false;
    }
    return slot < '17:00';
  }

  validateInvitations(): void {
    this.validatedInvitations.set(this.invitationListForm.getRawValue())
    this.showPlanning.set(true)
  }

  /**
   * Display planning availabilities
   * @param erpIds Ids of all prestations to plan
   * @param invitations Ids of all prestations to plan
   */
  private async updatePlanning(erpIds: number[], invitations: Invitation[]): Promise<void> {
    this.updatePlanningProgress.set(true);
    const guestData = {
      nbGuest: invitations.length,
      hasMale: invitations.some(g => g.gender !== Gender.Female)
    };
    const selectedPeriod = this.selectedPeriod();
    const { availabilities, durations } = await this.accountService.availabilitySpaBooking(
      this.center().erpId,
      erpIds,
      selectedPeriod.from.toISOString().slice(0, 10),
      selectedPeriod.to.toISOString().slice(0, 10),
      guestData
    );
    const availableDays = availabilities.map(a => ({
      from: new Date(`${a.date}T00:00:00Z`),
      to: new Date(`${a.date}T00:00:00Z`),
      css: ['border', '!text-white', '!bg-100ciels-300'],
      data: { availabilities: a, durations }
    } as Custom<Date>));
    this.availableDays.set(availableDays);
    this.updatePlanningProgress.set(false);
  }
}
